/*
 * Copyright (C) 2020 Graylog, Inc.
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the Server Side Public License, version 1,
 * as published by MongoDB, Inc.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * Server Side Public License for more details.
 *
 * You should have received a copy of the Server Side Public License
 * along with this program. If not, see
 * <http://www.mongodb.com/licensing/server-side-public-license>.
 */
import * as React from 'react';
import styled from 'styled-components';
import { useState, useMemo, useCallback } from 'react';

import SelectedDataLakesContext from 'data-lake/contexts/SelectedDataLakesContext';
import { DropdownButton, MenuItem, DeleteMenuItem, ButtonToolbar } from 'components/bootstrap';
import PreviewExternalLakeLogsButton from 'data-lake/components/PreviewExternalLakeLogsButton';
import RetrievalArchiveButton from 'data-lake/data-lake-overview/actions/RetrievalArchiveButton';
import RetrievalArchiveModal from 'data-lake/data-lake-overview/actions/RetrievalArchiveModal';
import { EXTERNAL_DATA_LAKE_STORAGE_TYPE_ATHENA } from 'data-lake/preview/storage-types/external/Constants';
import { EXTERNAL_DATA_LAKE_TYPE } from 'data-lake/Constants';
import { IfPermitted } from 'components/common';
import useSendTelemetry from 'logic/telemetry/useSendTelemetry';
import useLocation from 'routing/useLocation';
import { TELEMETRY_EVENT_TYPE } from 'telemetry/Constants';
import { getPathnameWithoutId } from 'util/URLUtils';

import { ExternalDataLakeInputType } from './DataLakeConnectorCreateInputModal';

const StyledButtonToolbar = styled(ButtonToolbar)`
  justify-content: flex-end;
`;

type Props = {
  onEdit: () => void;
  onDelete: () => void;
  onCreateInput: () => void;
  dataLakeId: string;
  dataLakeTitle?: string;
};

const DataLakeConnectorActions = ({
  onEdit,
  onDelete,
  onCreateInput,
  dataLakeId,
  dataLakeTitle = undefined,
}: Props) => {
  const [showRetrievalModal, setShowRetrievalModal] = useState(false);
  const toggleRetrievalModal = useCallback(() => setShowRetrievalModal((cur) => !cur), []);
  const selectedDataLake = useMemo(
    () => ({
      selectedDataLake: {
        id: dataLakeId,
        storageType: EXTERNAL_DATA_LAKE_STORAGE_TYPE_ATHENA,
        type: EXTERNAL_DATA_LAKE_TYPE,
        active: true,
        title: dataLakeTitle,
      },
    }),
    [dataLakeId, dataLakeTitle],
  );
  const sendTelemetry = useSendTelemetry();
  const { pathname } = useLocation();
  const appPathname = useMemo(() => getPathnameWithoutId(pathname), [pathname]);

  const handlePreviewLogsClick = useCallback(() => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.DATALAKE.CONNECTORS.PREVIEW_LOGS_BUTTON_CLICKED, {
      app_pathname: appPathname,
    });
  }, [appPathname, sendTelemetry]);

  const handleRetrieveLogsClick = useCallback(() => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.DATALAKE.CONNECTORS.RETRIEVE_LOGS_BUTTON_CLICKED, {
      app_pathname: appPathname,
    });
    toggleRetrievalModal();
  }, [appPathname, sendTelemetry, toggleRetrievalModal]);

  const handleEdit = useCallback(() => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.DATALAKE.CONNECTORS.EDIT_CONNECTOR_CLICKED, {
      app_pathname: appPathname,
    });
    onEdit();
  }, [appPathname, onEdit, sendTelemetry]);

  const handleCreateInput = useCallback(() => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.DATALAKE.CONNECTORS.CREATE_INPUT_CLICKED, {
      app_pathname: appPathname,
    });
    onCreateInput();
  }, [appPathname, onCreateInput, sendTelemetry]);

  const handleDelete = useCallback(() => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.DATALAKE.CONNECTORS.DELETE_CONNECTOR_CLICKED, {
      app_pathname: appPathname,
    });
    onDelete();
  }, [appPathname, onDelete, sendTelemetry]);

  return (
    <StyledButtonToolbar>
      <PreviewExternalLakeLogsButton dataLakeId={dataLakeId} onClick={handlePreviewLogsClick} />
      <RetrievalArchiveButton
        onClick={handleRetrieveLogsClick}
        buttonType="button"
        dataLakeType={EXTERNAL_DATA_LAKE_TYPE}
        dataLakeStorageType={EXTERNAL_DATA_LAKE_STORAGE_TYPE_ATHENA}
      />
      <DropdownButton bsSize="xs" title="More" pullRight>
        <MenuItem onSelect={handleEdit}>Edit</MenuItem>
        <IfPermitted permissions={[`inputs:create:${ExternalDataLakeInputType}`]}>
          <MenuItem onSelect={handleCreateInput}>Create Input</MenuItem>
        </IfPermitted>
        <IfPermitted permissions={['data_lake_archive:delete']}>
          <DeleteMenuItem onSelect={handleDelete}>Delete</DeleteMenuItem>
        </IfPermitted>
      </DropdownButton>
      {showRetrievalModal && (
        <SelectedDataLakesContext.Provider value={selectedDataLake}>
          <RetrievalArchiveModal onToggleModal={toggleRetrievalModal} calculateEstimatedSize={false} />
        </SelectedDataLakesContext.Provider>
      )}
    </StyledButtonToolbar>
  );
};

export default DataLakeConnectorActions;
